import React, { useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { Dropdown } from 'primereact/dropdown';
import { useLanguage } from '../../../../contexts/LanguageContext';

const CreditCardForm = ({
    cardNumber,
    cardHolderName,
    expiryMonth,
    expiryYear,
    cvv,
    maskedCardNumber,
    cardInfo,
    monthOptions,
    yearOptions,
    onCardNumberChange,
    onFieldChange,
    errors,
    submitted,
    disabled,
    isValidatingCard
}) => {
    const { th } = useLanguage();
    const [showBack, setShowBack] = useState(false);

    // Kart tipi ikonu
    const cardTypeIcon = useMemo(() => {
        if (!cardInfo?.cardType) return 'pi pi-credit-card';

        switch (cardInfo.cardType.toLowerCase()) {
            case 'visa':
                return 'pi pi-credit-card text-blue-500';
            case 'mastercard':
                return 'pi pi-credit-card text-orange-500';
            case 'troy':
                return 'pi pi-credit-card text-red-500';
            default:
                return 'pi pi-credit-card';
        }
    }, [cardInfo]);

    // Kart numarası formatla (4 haneli gruplar)
    const formatCardNumber = (value) => {
        if (!value) return '';
        const clean = value.replace(/\D/g, '');
        const groups = clean.match(/.{1,4}/g) || [];
        return groups.join(' ');
    };

    // Kart numarası input handler
    const handleCardNumberInput = (e) => {
        const value = e.target.value.replace(/\s/g, '');
        if (value.length <= 16) {
            onCardNumberChange(value);
        }
    };

    // CVV focus/blur handlers
    const handleCvvFocus = () => setShowBack(true);
    const handleCvvBlur = () => setShowBack(false);

    return (
        <div className="surface-card p-4 border-round-xl shadow-1 mb-4">
            <div className="flex align-items-center mb-4">
                <i className="pi pi-credit-card text-2xl text-primary mr-3"></i>
                <h3 className="m-0 text-xl font-semibold">{th('creditCardCollection.sections.cardInfo') || 'Kart Bilgileri'}</h3>
            </div>

            {/* Kart Önizleme - Ön ve Arka Yüz */}
            <div className="mb-4" style={{ perspective: '1000px' }}>
                <div
                    className="relative"
                    style={{
                        maxWidth: '400px',
                        height: '200px',
                        transformStyle: 'preserve-3d',
                        transition: 'transform 0.6s',
                        transform: showBack ? 'rotateY(180deg)' : 'rotateY(0deg)'
                    }}
                >
                    {/* Kartın Ön Yüzü */}
                    <div
                        className="border-round-xl p-4 absolute w-full h-full"
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            backfaceVisibility: 'hidden'
                        }}
                    >
                        {/* Kart Tipi */}
                        <div className="flex justify-content-between align-items-start mb-4">
                            <div className="text-white opacity-80">
                                <i className="pi pi-wifi text-2xl" style={{ transform: 'rotate(90deg)' }}></i>
                            </div>
                            <div className="text-white">
                                {cardInfo?.cardType && (
                                    <span className="text-lg font-bold">{cardInfo.cardType}</span>
                                )}
                            </div>
                        </div>

                        {/* Kart Numarası */}
                        <div className="text-white text-xl font-mono mb-4 letter-spacing-2">
                            {maskedCardNumber || '•••• •••• •••• ••••'}
                        </div>

                        {/* Kart Sahibi ve Son Kullanma */}
                        <div className="flex justify-content-between align-items-end">
                            <div>
                                <div className="text-white-alpha-70 text-xs mb-1">
                                    {th('creditCardCollection.fields.cardHolder') || 'KART SAHİBİ'}
                                </div>
                                <div className="text-white font-semibold text-sm uppercase">
                                    {cardHolderName || 'AD SOYAD'}
                                </div>
                            </div>
                            <div>
                                <div className="text-white-alpha-70 text-xs mb-1">
                                    {th('creditCardCollection.fields.expiry') || 'SON KULLANMA'}
                                </div>
                                <div className="text-white font-semibold text-sm">
                                    {expiryMonth || 'AA'}/{expiryYear?.slice(-2) || 'YY'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Kartın Arka Yüzü */}
                    <div
                        className="border-round-xl absolute w-full h-full"
                        style={{
                            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                            backfaceVisibility: 'hidden',
                            transform: 'rotateY(180deg)'
                        }}
                    >
                        {/* Manyetik Şerit */}
                        <div
                            className="w-full mt-4"
                            style={{
                                height: '45px',
                                background: '#1a1a2e'
                            }}
                        ></div>

                        {/* İmza Alanı ve CVV */}
                        <div className="px-4 mt-4">
                            <div className="flex align-items-center gap-3">
                                {/* İmza Alanı */}
                                <div
                                    className="flex-1 border-round"
                                    style={{
                                        height: '40px',
                                        background: 'repeating-linear-gradient(45deg, #f5f5f5, #f5f5f5 5px, #e0e0e0 5px, #e0e0e0 10px)'
                                    }}
                                ></div>

                                {/* CVV Kutusu */}
                                <div
                                    className="flex align-items-center justify-content-center border-round"
                                    style={{
                                        width: '60px',
                                        height: '40px',
                                        background: '#fff',
                                        border: '1px solid #ddd'
                                    }}
                                >
                                    <span className="font-mono font-bold text-900">
                                        {cvv || '***'}
                                    </span>
                                </div>
                            </div>

                            {/* CVV Label */}
                            <div className="text-right mt-2">
                                <span className="text-white-alpha-70 text-xs">CVV</span>
                            </div>
                        </div>

                        {/* Kart Tipi - Arka Yüz */}
                        <div className="absolute" style={{ bottom: '16px', right: '16px' }}>
                            <span className="text-white font-bold">{cardInfo?.cardType || ''}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid">
                {/* Kart Numarası */}
                <div className="col-12">
                    <label htmlFor="cardNumber" className="block text-900 font-medium mb-2">
                        {th('creditCardCollection.fields.cardNumber') || 'Kart Numarası'} <span className="text-red-500">*</span>
                    </label>
                    <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">
                            <i className={cardTypeIcon}></i>
                        </span>
                        <InputText
                            id="cardNumber"
                            value={formatCardNumber(cardNumber)}
                            onChange={handleCardNumberInput}
                            className={submitted && errors?.cardNumber ? 'p-invalid' : ''}
                            placeholder="0000 0000 0000 0000"
                            maxLength={19}
                            disabled={disabled}
                        />
                        {isValidatingCard && (
                            <span className="p-inputgroup-addon">
                                <i className="pi pi-spin pi-spinner"></i>
                            </span>
                        )}
                    </div>
                    {submitted && errors?.cardNumber && <small className="p-error block mt-1">{errors.cardNumber}</small>}
                </div>

                {/* Kart Sahibi Adı */}
                <div className="col-12">
                    <label htmlFor="cardHolderName" className="block text-900 font-medium mb-2">
                        {th('creditCardCollection.fields.cardHolderName') || 'Kart Üzerindeki İsim'} <span className="text-red-500">*</span>
                    </label>
                    <InputText
                        id="cardHolderName"
                        value={cardHolderName}
                        onChange={(e) => onFieldChange('cardHolderName', e.target.value.toUpperCase())}
                        className={`w-full ${submitted && errors?.cardHolderName ? 'p-invalid' : ''}`}
                        placeholder={th('creditCardCollection.placeholders.cardHolderName') || 'Kart üzerindeki ismi giriniz'}
                        disabled={disabled}
                        style={{ textTransform: 'uppercase' }}
                    />
                    {submitted && errors?.cardHolderName && <small className="p-error block mt-1">{errors.cardHolderName}</small>}
                </div>

                {/* Son Kullanma Tarihi */}
                <div className="col-12 md:col-4">
                    <label htmlFor="expiryMonth" className="block text-900 font-medium mb-2">
                        {th('creditCardCollection.fields.expiryMonth') || 'Ay'} <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                        id="expiryMonth"
                        value={expiryMonth}
                        options={monthOptions}
                        onChange={(e) => onFieldChange('expiryMonth', e.value)}
                        placeholder={th('creditCardCollection.placeholders.month') || 'Ay'}
                        className={`w-full ${submitted && errors?.expiryMonth ? 'p-invalid' : ''}`}
                        disabled={disabled}
                    />
                    {submitted && errors?.expiryMonth && <small className="p-error block mt-1">{errors.expiryMonth}</small>}
                </div>

                <div className="col-12 md:col-4">
                    <label htmlFor="expiryYear" className="block text-900 font-medium mb-2">
                        {th('creditCardCollection.fields.expiryYear') || 'Yıl'} <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                        id="expiryYear"
                        value={expiryYear}
                        options={yearOptions}
                        onChange={(e) => onFieldChange('expiryYear', e.value)}
                        placeholder={th('creditCardCollection.placeholders.year') || 'Yıl'}
                        className={`w-full ${submitted && errors?.expiryYear ? 'p-invalid' : ''}`}
                        disabled={disabled}
                    />
                    {submitted && errors?.expiryYear && <small className="p-error block mt-1">{errors.expiryYear}</small>}
                </div>

                {/* CVV */}
                <div className="col-12 md:col-4">
                    <label htmlFor="cvv" className="block text-900 font-medium mb-2">
                        CVV <span className="text-red-500">*</span>
                    </label>
                    <div className="p-inputgroup">
                        <InputMask
                            id="cvv"
                            value={cvv}
                            onChange={(e) => onFieldChange('cvv', e.value)}
                            mask="999"
                            placeholder="***"
                            className={submitted && errors?.cvv ? 'p-invalid' : ''}
                            disabled={disabled}
                            onFocus={handleCvvFocus}
                            onBlur={handleCvvBlur}
                        />
                        <span className="p-inputgroup-addon">
                            <i className="pi pi-lock"></i>
                        </span>
                    </div>
                    {submitted && errors?.cvv && <small className="p-error block mt-1">{errors.cvv}</small>}
                </div>
            </div>
        </div>
    );
};

export default React.memo(CreditCardForm);
